import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiDashboardPermission,
  BiDashboardSummary,
  BiDashboardVisibility,
} from '@euler/model/bi/dashboard';
import {
  LayoutDashboard,
  Lock,
  Pencil,
  Trash2,
  Users,
} from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { BiButton } from '../components/ui/BiButton';
import { BiSurfaceCard } from '../components/ui/BiSurfaceCard';
import { BiTag } from '../components/ui/BiTag';
import {
  biTimeLabel,
  kBiPermissionLabels,
  kBiVisibilityTags,
} from '../functions/biDashboardText';

/** One dashboard in the list; its owner may rename or delete it here. */
export const DashboardTile = memo(
  ({
    item,
    onOpen,
    onRename,
    onDelete,
  }: {
    item: BiDashboardSummary;
    onOpen: () => void;
    onRename: () => void;
    onDelete: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const owner = item.permission === BiDashboardPermission.Manage;
    const shared = item.visibility === BiDashboardVisibility.Store;
    const facts = [
      `${item.cardCount} 张图表`,
      `更新于 ${biTimeLabel(item.updatedAt)}`,
    ];
    if (!owner && item.ownerName) facts.unshift(`来自 ${item.ownerName}`);
    return (
      <BiSurfaceCard
        onPress={onOpen}
        accessibilityLabel={`打开看板 ${item.name}`}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(12),
          padding: rpx(14),
        }}
      >
        <View
          style={{
            width: rpx(40),
            height: rpx(40),
            borderRadius: rpx(10),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tint(aiColors.amber, 0.14),
          }}
        >
          <LayoutDashboard size={20} color={aiColors.amber} />
        </View>
        <View style={{ flex: 1, gap: rpx(4) }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: rpx(6),
            }}
          >
            <Typography.Text
              medium
              size={rpx(14)}
              color={aiColors.foreground}
              numberOfLines={1}
              style={{ flexShrink: 1 }}
            >
              {item.name}
            </Typography.Text>
            {item.isDefault ? (
              <BiTag text="默认" color={aiColors.primary} />
            ) : null}
            <BiTag
              text={kBiVisibilityTags[item.visibility]}
              color={shared ? aiColors.green : aiColors.mutedForeground}
              icon={shared ? Users : Lock}
            />
            {!owner ? (
              <BiTag
                text={kBiPermissionLabels[item.permission]}
                color={aiColors.violet}
              />
            ) : null}
          </View>
          <Typography.Text
            size={rpx(12)}
            color={aiColors.mutedForeground}
            numberOfLines={1}
          >
            {facts.join(' · ')}
          </Typography.Text>
        </View>
        {owner ? (
          <View style={{ flexDirection: 'row', gap: rpx(4) }}>
            <BiButton
              icon={Pencil}
              tone="quiet"
              accessibilityLabel={`重命名 ${item.name}`}
              onPress={onRename}
            />
            <BiButton
              icon={Trash2}
              tone="quiet"
              accessibilityLabel={`删除 ${item.name}`}
              onPress={onDelete}
            />
          </View>
        ) : null}
      </BiSurfaceCard>
    );
  },
);
